import { useEffect } from 'react'
import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import Card from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import CheckCircle from "@mui/icons-material/CheckCircle";
import SectionsIcon from "@/assets/sections.svg";
import useOnboardingContext from "@/hooks/useOnboardingContext";
import FormContainer from "./FormContainer";
import { STEPS } from "@/constants/onboarding";
import WizardFooter from "./WizardFooter";
import FullStepHeader from "../FullStepHeader";
import { useContext } from 'react'
import { OnboardingContext } from '@/context/OnboardingContext'
import getSupportedSections from "@/templates/supported-sections"
import { getProvisionedResources } from '@/api/onboarding'
import { OLD_GRID_LAYOUT_MENU } from '@/constants/onboarding'

const CustomCard = styled(Card)`
  max-width: 100vw;
  background: #FFFFFF;
  border: 2px solid ${({ theme }) => theme.palette.primary.main};
  padding: 24px;
  gap: 24px;
  border-radius: 12px;
  margin-bottom: 12px;
`;

const WizardStep9 = () => {
  const { actualStep, actualSubStep, sections, setSections, storeOnboardingData: { isStoreOnboardingComplete, appChoices }, recommendedApps, selectedTemplate, setSku, sku, isGrid } = useOnboardingContext();
  const { templateCategoryData } = useContext(OnboardingContext)
  const DefaultSupportedSections = getSupportedSections(selectedTemplate?.id, sku, isGrid)

  useEffect(()=>{
      getProvisionedResources()
      .then(res => {
        if (res) {
          setSku(res.ProvisionedResources[0]?.Sku)
        }
      })
  }, [])

  const sectionMappings = {
    'Appointments': 'Services',
    'Contact Form': 'Contact Form',
    'Testimonials': 'Testimonials',
    'About': 'About',
    'Products': 'Products'
  };

  const sortedSupportedSections = [
    // Keep header_and_introduction first
    DefaultSupportedSections[0],
    // Sort the rest of the sections except footer
    ...DefaultSupportedSections.slice(1)
      .filter(section => section.id !== 'footer')
      .sort((a, b) => {
        // Check if websiteSections exists and is already an array
        const sectionsArray = Array.isArray(templateCategoryData?.websiteSections) 
          ? templateCategoryData.websiteSections 
          : [];
        
        // Find the template section name that maps to each section label
        const aTemplateSection = Object.keys(sectionMappings).find(key => sectionMappings[key] === a.label);
        const bTemplateSection = Object.keys(sectionMappings).find(key => sectionMappings[key] === b.label);
        
        const aIndex = sectionsArray.findIndex(section => section === aTemplateSection);
        const bIndex = sectionsArray.findIndex(section => section === bTemplateSection);
        
        // If section not found in templateCategoryData, put it at the end
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      }),
    // Add footer as the last section
    DefaultSupportedSections.find(section => section.id === 'footer')
  ];
  // Check if all recommended apps would be disabled
  const previousAppChoices = JSON.parse(appChoices ?? "{}")
  const allAppsDisabled = recommendedApps.every((app) => {
    if (isStoreOnboardingComplete) {
      return true; // All apps would be disabled
    }
    /* if (previousAppChoices[app.id]) {
      return true; // App would be disabled
    } */
    return false; // App would not be disabled
  }) && recommendedApps.length > 0;

  // Skip apps steps if all apps are disabled
  const skipAppsSteps = allAppsDisabled;

  const step = STEPS[actualStep];
  const subStep = step?.subSteps && step.subSteps[actualSubStep];
  const {
    wizard: { title, subtitle },
  } = subStep || step;

  const handleChange = (event) => {
    setSections((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  return (
    <Box sx={{ backgroundColor: "#F3F4F8", height: '100vh', width: '100%' }} className="md:overflow-hidden">
      <Box className="fixed top-0 left-0 right-0">
        <FullStepHeader />
      </Box>
      <Box className="w-full flex items-center justify-center h-screen h-screen">
        <Box>
          <Box
            className="md:w-[438px] mt-4 md:mt-0"
            sx={{ maxWidth: "100vw", mb: 4, px: 2, textAlign: "center" }}
          >
            <FormContainer title={title} subtitle={subtitle}></FormContainer>
          </Box>
          <Box className='mx-6 md:mx-0 md:w-[438px]'>
            <CustomCard>
              <Box className="flex gap-5 mb-2">
                <img src={SectionsIcon} />
                <Box>
                  <Box className="flex justify-between">
                    <Typography className="!text-[16px]" variant="h5">Single Page Site</Typography>
                    <CheckCircle fontSize="small" color="primary.dark" />
                  </Box>
                  <Typography>
                    All your essential content, elegantly streamlined in one
                    engaging scroll.
                  </Typography>
                </Box>
              </Box>
              <FormControl component="fieldset">
                <Typography className="pt-3 pb-2 text-[14px]" sx={{ fontWeight: "bold" }} component="legend">
                  Include Homepage Sections
                </Typography>
                <FormGroup >
                  {sortedSupportedSections.map((section, index) => (
                    <FormControlLabel
                      key={index}
                      disabled={section.disabled}
                      control={
                        <Checkbox
                          sx={{
                            "&.Mui-checked": {
                            color: "primary.dark"
                            },
                            "&.Mui-disabled": {
                              color: "rgba(0,0,0,0.26)"
                            }
                          }}
                          name={section.id}
                          checked={sections[section.id]}
                          onChange={handleChange}
                          className="!py-2"
                        />
                      }
                      label={section.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </CustomCard>
            <Box className="fixed bottom-0 left-0 right-5 md:static">
              <WizardFooter skip={skipAppsSteps} customText={skipAppsSteps? {
                  text: "Next",
                  next: "Review"
              } : {}} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WizardStep9;
